import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { rupees, formatIndian, purityColor, formatApiError, formatWeight } from "@/lib/utils";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Info, MessageCircle, Trash2, Edit, AlertTriangle, Share2, ImageOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ProductEditModal from "@/components/ProductEditModal";
import DiscountCalculator from "@/components/DiscountCalculator";
import MemoBadge from "@/components/MemoBadge";
import { AshthLiteBadge } from "@/components/AshthLiteBadge";
import PhotoPlaceholder from "@/components/PhotoPlaceholder";
import { shareToWhatsApp } from "@/lib/whatsappShare";
import { downloadSingleCard } from "@/lib/shareCard";
import { PublicDeleteDialog, deletePinErrorMessage } from "@/components/DeletePinControls";

/**
 * level: 'company' | 'franchise_owner' | 'franchise_team' (default 'company')
 *   For franchise shareable links pass token + view (staff/owner) instead.
 */
export default function ProductDetailDrawer({ jewelCode, token, tokenKind = "franchise", view = "staff", pin, kind = "product", level: propLevel, onClose, onDeleted, onEdited }) {
  const [data, setData] = useState(null);
  const [showDiamonds, setShowDiamonds] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const { user } = useAuth();
  const canManage = !token && user?.role === "master_admin";
  // CS 21.86 -- public owner-unlocked delete (franchise/distributor links only;
  // never retailer/staff/plain public views). A fresh delete PIN is required
  // per deletion via PublicDeleteDialog; the server independently verifies it.
  const [publicDeleteOpen, setPublicDeleteOpen] = useState(false);
  const [publicDeleteBusy, setPublicDeleteBusy] = useState(false);
  const canPublicDelete = !!token && kind === "product" && view === "owner" && !!pin
    && (tokenKind === "franchise" || tokenKind === "distributor");

  // Determine effective level for column visibility
  const level = useMemo(() => {
    if (token) {
      if (tokenKind === "distributor") return view === "owner" ? "distributor_owner" : "distributor_team";
      if (tokenKind === "retailer") return view === "owner" ? "retailer_owner" : "retailer_staff";
      return view === "owner" ? "franchise_owner" : "franchise_team";
    }
    return propLevel || "company";
  }, [token, tokenKind, view, propLevel]);

  // What columns are visible to this viewer? Note: actual rendering is also gated
  // by whether the backend returned the tier data (e.g., franchise channel never gets retailer).
  const showCompany = level === "company";
  const showFranchise = level === "company" || level === "franchise_owner" || level === "distributor_owner";

  useEffect(() => {
    if (!jewelCode) return;
    setData(null);
    // CS 21.77 -- for design mode we thread `kind=design` through the public
    // detail endpoint so the STOCK_ONLY_FILTER doesn't return 404, and the
    // resolver keys on style_no/id instead of jewel_code (designs never
    // have one). Product detail stays untouched (default kind=product).
    const qs = new URLSearchParams();
    if (view === "owner" && pin) { qs.set("view", "owner"); qs.set("pin", pin); }
    if (kind === "design") qs.set("kind", "design");
    const qstr = qs.toString();
    const url = token
      ? `/public/${tokenKind}/${token}/product/${encodeURIComponent(jewelCode)}${qstr ? `?${qstr}` : ""}`
      : `/products/${encodeURIComponent(jewelCode)}`;
    api.get(url)
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error(formatApiError(e.response?.data?.detail) || "Product not found");
        onClose?.();
      });
  }, [jewelCode, token, tokenKind, view, pin, kind, onClose, reloadKey]);

  const doDelete = async () => {
    try {
      await api.delete(`/products/${encodeURIComponent(jewelCode)}`);
      toast.success("Product moved to Trash");
      setConfirmDelete(false);
      onDeleted?.(jewelCode);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  // CS 21.86 -- single-piece delete from an owner-unlocked public link.
  const doPublicDelete = async (pinValue) => {
    setPublicDeleteBusy(true);
    try {
      const { data: r } = await api.post(`/public/${tokenKind}/${token}/products/delete`, {
        jewel_codes: [jewelCode],
        pin: pinValue,
      });
      if ((r.rejected_out_of_scope || []).length) {
        toast.error("This piece is out of scope for this link");
      } else {
        toast.success("Piece deleted");
        setPublicDeleteOpen(false);
        onDeleted?.(jewelCode);
        onClose?.();
      }
    } catch (e) {
      toast.error(deletePinErrorMessage(e));
    } finally {
      setPublicDeleteBusy(false);
    }
  };

  return (
    <Sheet open={!!jewelCode} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <SheetContent side="right" className="w-full sm:max-w-3xl lg:max-w-4xl overflow-y-auto bg-[#FAF8F3] border-l-[#0D1B2A]/10" data-testid="product-detail-drawer">
        <SheetHeader className="text-left">
          <div className="flex items-start justify-between gap-3 pr-10 flex-wrap">
            <SheetTitle className="font-serif text-3xl text-[#0D1B2A]">Product Detail</SheetTitle>
            {canPublicDelete && data && (
              <button
                onClick={() => setPublicDeleteOpen(true)}
                data-testid="public-delete-product-btn"
                title="Delete this piece (delete PIN required)"
                className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600 transition-colors"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.8} />
              </button>
            )}
            {canManage && data && (
              <div className="flex gap-2 items-center" data-testid="product-actions">
                <button
                  onClick={() => setEditOpen(true)}
                  data-testid="edit-product-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-[#C9A84C] text-[#C9A84C] hover:bg-[#C9A84C]/10 transition-colors"
                >
                  <Edit className="w-3.5 h-3.5" strokeWidth={1.8} /> Edit
                </button>
                <button
                  onClick={() => setConfirmDelete(true)}
                  data-testid="delete-product-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-red-400 text-red-600 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} /> Delete
                </button>
              </div>
            )}
          </div>
        </SheetHeader>
        {!data ? (
          <div className="space-y-3 mt-6">
            <div className="skeleton h-12" />
            <div className="skeleton h-40" />
            <div className="skeleton h-60" />
          </div>
        ) : (
          <Body
            data={data}
            showCompany={showCompany}
            showFranchise={showFranchise}
            showDiamonds={showDiamonds}
            setShowDiamonds={setShowDiamonds}
            token={token}
            tokenKind={tokenKind}
            view={view}
            pin={pin}
          />
        )}

        <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Move to Trash?</DialogTitle></DialogHeader>
            <div className="flex items-start gap-3 text-sm">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[#0D1B2A]"><strong>Jewel Code:</strong> {data?.product?.jewel_code}</p>
                <p className="text-[#0D1B2A]"><strong>Style:</strong> {data?.product?.style_no}</p>
                <p className="text-slate-500 mt-2">This product will be moved to Trash. It can be restored or permanently deleted from there.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDelete(false)}>Cancel</Button>
              <Button onClick={doDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">Move to Trash</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <PublicDeleteDialog
          open={publicDeleteOpen}
          onOpenChange={setPublicDeleteOpen}
          codes={[jewelCode]}
          busy={publicDeleteBusy}
          onConfirm={doPublicDelete}
          testidPrefix="public-delete"
          scopeNote={tokenKind === "distributor"
            ? "Removes this piece from the distributor's stock — it disappears from both the Distributor and Retailer links."
            : "Removes this piece from the main Ashth catalogue — it disappears from HO and every share link. HO can restore it from Trash."}
        />

        <ProductEditModal jewelCode={jewelCode} open={editOpen}
          onClose={() => setEditOpen(false)}
          onSaved={(newJc) => { setReloadKey(reloadKey + 1); onEdited?.(newJc); }} />
      </SheetContent>
    </Sheet>
  );
}

function Body({ data, showCompany, showFranchise, showDiamonds, setShowDiamonds,
                token, tokenKind, view, pin }) {
  const p = data.product;
  const pr = data.pricing;
  const rm = data.rate_meta;

  // CS 17 — Distributor channel surfaces an extra Retailer Cost column and relabels
  // the franchise tier as "Distributor Cost". For franchise/HO/unassigned products
  // the historical 3-column layout (Company + Franchise + Customer) is preserved.
  const isDistributorChannel = pr.channel === "distributor";

  const columns = useMemo(() => {
    const cols = [];
    if (showCompany && pr.company) cols.push({ key: "company", label: "Company Cost", data: pr.company });
    if (showFranchise && pr.franchise) {
      cols.push({
        key: "franchise",
        label: isDistributorChannel ? "Distributor Cost" : "Franchise",
        data: pr.franchise,
      });
    }
    if (isDistributorChannel && pr.retailer) {
      cols.push({ key: "retailer", label: "Retailer Cost", data: pr.retailer });
    }
    cols.push({ key: "customer", label: "Customer MRP", data: pr.customer, accent: true });
    return cols;
  }, [showCompany, showFranchise, isDistributorChannel, pr.company, pr.franchise, pr.retailer, pr.customer]);

  const showRetailer = isDistributorChannel && !!pr.retailer;

  // CS 21.80 — AL-series visibility: backend sets `al_final_only` and has
  // already removed every breakup field from this payload. Render ONLY the
  // final price lines (big + clean, same layout as the retailer shopfront
  // price section). Non-AL pieces are untouched.
  const alFinalOnly = !!pr.al_final_only;
  const alPriceLabels = {
    company: "Company Cost",
    franchise: isDistributorChannel ? "Distributor Cost" : "Franchise Cost",
    retailer: "Retailer Price",
    customer: "Suggested MRP",
  };

  // Diamond lines with unrecognised shapes — memoised; touched twice in JSX below.
  const unrecognisedLines = useMemo(
    () => (p.diamond_lines || []).filter((l) => l.unrecognized),
    [p.diamond_lines]
  );
  const unrecognisedSummary = useMemo(
    () => unrecognisedLines.map((l) => l.raw_shape || l.shape_code.replace(/^UNKNOWN:/, "")).join(", "),
    [unrecognisedLines]
  );
  const hasShapeWarning = p.has_shape_warning || unrecognisedLines.length > 0;

  const gstPct = ((rm.gst_rate || 0.03) * 100).toFixed(0);

  const shareWhatsApp = () => {
    const today = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    const totalCt = formatIndian(pr.total_diamond_ct, { decimals: 3 });
    const totalPcs = formatIndian(pr.total_diamond_pcs);
    const avgRate = pr.customer.avg_dia_rate || 0;
    const metalLabel = rm.metal_label || "Gold";
    const metalRate = rm.metal_rate || rm.gold_rate;
    const lines = [
      "*Ashth Pricing System*",
      "─────────────────────",
      `*Product:* ${p.jewel_code || p.style_no}`,
      `*Style:* ${p.style_no}`,
      `*Category:* ${p.category}${p.sub_category ? ` · ${p.sub_category}` : ""}`,
      `*Metal:* ${p.purity} ${metalLabel} (${formatWeight(p.net_weight)} g)`,
      `*Diamonds:* ${totalPcs} pcs · ${totalCt} ct`,
    ];
    if (alFinalOnly) {
      // CS 21.80 — AL-series share: final price only, no breakup lines.
      lines.push(
        "",
        "─────────────────────",
        `*Customer Price: ${rupees(pr.customer.total)}*`,
        "",
        `_Prices valid as of: ${today}_`,
      );
    } else {
      lines.push(
        "",
        "─────────────────────",
        "*PRICE BREAKDOWN*",
        "",
        `Metal (${p.purity}, ${formatWeight(p.net_weight)}g): ${rupees(pr.metal_cost)}`,
        `Labour: ${rupees(pr.customer.labour)}`,
        `Diamond (${totalCt} ct): ${rupees(pr.customer.diamond)}`,
        `Sub-total: ${rupees(pr.customer.subtotal)}`,
        `GST (${gstPct}%): ${rupees(pr.customer.gst)}`,
        "─────────────────────",
        `*Customer Price: ${rupees(pr.customer.total)}*`,
        "_(Rounded to nearest ₹100)_",
        "",
        `_Avg Diamond Rate: ${rupees(avgRate)}/ct_`,
        `_${metalLabel} Rate: ${rupees(metalRate)}/g_`,
        `_Prices valid as of: ${today}_`,
      );
    }
    shareToWhatsApp({ text: lines.join("\n"), photoUrl: p.photo_url || undefined });
  };

  // CS 21.27 -- Server-rendered card share (JPEG). Only wired on the two
  // distributor_stock-backed public links (kinds: distributor, retailer).
  const canShareCard = !!token && (tokenKind === "distributor" || tokenKind === "retailer");
  const [shareCardOpen, setShareCardOpen] = useState(false);
  const [shareWithPrice, setShareWithPrice] = useState(true);
  const [shareBusy, setShareBusy] = useState(false);

  const doShareCard = async () => {
    if (!data?.product) return;
    setShareBusy(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL
        ? `${process.env.REACT_APP_BACKEND_URL}/api`
        : "/api";
      await downloadSingleCard({
        apiBase, tokenKind, token,
        jewelCode: data.product.jewel_code,
        view, pin,
        withPrice: shareWithPrice,
      });
      toast.success("Card downloaded — forward via WhatsApp / email");
      setShareCardOpen(false);
    } catch (e) {
      toast.error(formatApiError(e?.message) || "Share failed");
    } finally {
      setShareBusy(false);
    }
  };

  return (
    <div className="space-y-8 mt-6 pb-12" data-testid="product-detail-body">
      {pr?.price_unavailable && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="price-unavailable-banner">
          Price unavailable — flagged for HO review
          {pr.unpriceable_reason === "unknown_metal_purity" ? ` (unrecognised metal/purity ${pr.unpriced_purity || ""})` : ""}
          {pr.unpriceable_reason === "diamond_line_unpriced" ? ` (no configured rate for: ${(pr.unpriced_shapes || []).join(", ")})` : ""}
        </div>
      )}

      {/* CS 21.27 -- Share-card modal */}
      {canShareCard && (
        <Dialog open={shareCardOpen} onOpenChange={setShareCardOpen}>
          <DialogContent className="bg-white max-w-md" data-testid="share-card-modal">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Share product card</DialogTitle>
            </DialogHeader>
            {data?.product && !data.product.photo_url && (
              <div className="flex items-start gap-3 rounded-md bg-amber-50 border-l-4 border-amber-400 p-3" data-testid="share-card-no-photo-warning">
                <ImageOff className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-900">
                  This piece has no photo — upload one for a better card. The share will still work, but the photo area will show a branded placeholder.
                </div>
              </div>
            )}
            <div className="space-y-4 py-2">
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={shareWithPrice}
                  onChange={(e) => setShareWithPrice(e.target.checked)}
                  data-testid="share-card-with-price"
                  className="w-4 h-4 accent-[#C9A84C]"
                />
                <div>
                  <div className="text-sm font-medium text-[#0D1B2A]">Include prices</div>
                  <div className="text-xs text-slate-500">
                    {tokenKind === "distributor"
                      ? "Retailer Price + MRP (never Distributor Cost)"
                      : "MRP only (Retailer Cost never shown to customer)"}
                  </div>
                </div>
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShareCardOpen(false)} disabled={shareBusy}>
                Cancel
              </Button>
              <Button
                onClick={doShareCard}
                disabled={shareBusy}
                className="bg-[#C9A84C] hover:bg-[#C9A84C]/90 text-white"
                data-testid="share-card-confirm-btn"
              >
                {shareBusy ? "Generating…" : "Share card"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Shape warning banner */}
      {hasShapeWarning && (
        <div className="luxe-card p-4 border-l-4 border-amber-400 bg-amber-50/50" data-testid="product-shape-warning">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm">
              <div className="font-semibold text-amber-900">Some diamond shapes are not in the system</div>
              <div className="text-amber-800 mt-1">
                Unrecognised: <span className="font-mono">{unrecognisedSummary}</span>. Diamond cost for these lines is set to ₹0. Edit the product to pick a known shape, or add the shape in <span className="font-semibold">Diamond Shapes</span> manager.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header info — CS 21.83 (v5): AL-series pieces get the image-led
          retailer-style layout (large photo, status badge, spec rows).
          Non-AL pieces keep the existing compact header unchanged. */}
      {alFinalOnly ? (
        <div className="luxe-card p-0 overflow-hidden" data-testid="al-detail-hero">
          <div className="relative bg-[#F7F5EE]">
            {p.photo_url ? (
              <img
                src={p.photo_url}
                alt={p.jewel_code || p.style_no || "product"}
                className="w-full max-h-[420px] object-contain mx-auto"
                data-testid="product-detail-photo"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="w-full h-64 sm:h-80 flex items-center justify-center" data-testid="product-detail-photo-placeholder">
                <PhotoPlaceholder size="lg" />
              </div>
            )}
            <div className="absolute top-3 left-3" data-testid="al-status-badge">
              {p.is_memo ? (
                <MemoBadge />
              ) : p.status === "sold" ? (
                <span className="text-[10px] uppercase tracking-wider px-3 py-1 rounded-full border border-red-300 bg-red-50 text-red-700 font-medium">Sold</span>
              ) : (
                <span className="text-[10px] uppercase tracking-wider px-3 py-1 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-700 font-medium">In Stock</span>
              )}
            </div>
          </div>
          <div className="p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C]">{p.category}{p.sub_category ? ` · ${p.sub_category}` : ""}</div>
                  <AshthLiteBadge product={p} size="xs" />
                </div>
                <h2 className="font-serif text-3xl text-[#0D1B2A] mt-1">{p.jewel_code || p.style_no || "—"}</h2>
                <div className="text-sm text-slate-500 mt-0.5">{p.jewel_code ? p.style_no : (p.jewel_code_ref || "")}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-[10px] uppercase tracking-wider px-3 py-1 rounded-full border ${purityColor(p.purity)}`}>{p.purity}</span>
                {canShareCard && (
                  <Button
                    size="sm"
                    onClick={() => setShareCardOpen(true)}
                    className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white"
                    data-testid="share-card-btn"
                  >
                    <Share2 className="w-4 h-4 mr-1.5" />
                    Share card
                  </Button>
                )}
              </div>
            </div>
            {/* Specification rows — physical specs only, never monetary.
                Weight label follows the metal (no misleading GOLD on PT950/925). */}
            <div className="mt-6 divide-y divide-[#0D1B2A]/8" data-testid="al-spec-rows">
              <div className="flex items-center justify-between py-3 text-sm">
                <span className="text-[10px] uppercase tracking-wider text-slate-400">Purity</span>
                <span className="font-medium text-[#0D1B2A]">{p.purity}</span>
              </div>
              <div className="flex items-center justify-between py-3 text-sm">
                <span className="text-[10px] uppercase tracking-wider text-slate-400">
                  {((p.purity || "").toUpperCase() === "PT950" ? "Platinum" : (p.purity || "").toUpperCase() === "925" ? "Silver" : "Gold")} Weight
                </span>
                <span className="font-medium text-[#0D1B2A] tabular-nums">{formatWeight(p.net_weight)} g</span>
              </div>
              <div className="flex items-center justify-between py-3 text-sm">
                <span className="text-[10px] uppercase tracking-wider text-slate-400">Diamonds</span>
                <span className="font-medium text-[#0D1B2A] tabular-nums">
                  {formatIndian(pr.total_diamond_pcs)} pcs · {formatIndian(pr.total_diamond_ct, { decimals: 3 })} ct
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
      <div className="luxe-card p-6" data-testid="product-detail-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          {/* CS 21.77 -- render the product/design photo here so the drawer
             looks IDENTICAL to a product detail. Designs never have
             jewel_code (null) so the primary heading falls back to style_no. */}
          {p.photo_url ? (
            <img
              src={p.photo_url}
              alt={p.jewel_code || p.style_no || "product"}
              className="w-24 h-24 sm:w-28 sm:h-28 rounded-lg object-cover border border-[#0D1B2A]/10 shrink-0"
              data-testid="product-detail-photo"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-lg border border-[#0D1B2A]/10 shrink-0 flex items-center justify-center bg-[#F7F5EE]" data-testid="product-detail-photo-placeholder">
              <PhotoPlaceholder size="sm" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C]">{p.category}{p.sub_category ? ` · ${p.sub_category}` : ""}</div>
              {p.is_memo && <MemoBadge />}
              <AshthLiteBadge product={p} size="xs" />
            </div>
            <h2 className="font-serif text-3xl text-[#0D1B2A] mt-1">{p.jewel_code || p.style_no || "—"}</h2>
            <div className="text-sm text-slate-500 mt-0.5">{p.jewel_code ? p.style_no : (p.jewel_code_ref || "")}</div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-[10px] uppercase tracking-wider px-3 py-1 rounded-full border ${purityColor(p.purity)}`}>{p.purity}</span>
            {canShareCard && (
              <Button
                size="sm"
                onClick={() => setShareCardOpen(true)}
                className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white"
                data-testid="share-card-btn"
              >
                <Share2 className="w-4 h-4 mr-1.5" />
                Share card
              </Button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-6 text-sm">
          <Meta label="Net Weight" value={`${formatWeight(p.net_weight)} g`} />
          <Meta label="Gross Weight" value={`${formatIndian(p.gross_weight, { decimals: 3 })} g`} />
          <Meta label="Diamond Pcs" value={(() => {
            const total = (p.diamond_lines || []).reduce((s, l) => s + (Number(l.pieces) || 0), 0);
            return total > 0 ? total.toString() : "—";
          })()} />
          <Meta label="Diamond Weight" value={(() => {
            const total = (p.diamond_lines || []).reduce((s, l) => s + (Number(l.weight_ct) || 0), 0);
            return total > 0 ? `${formatIndian(total, { decimals: 3 })} ct` : "—";
          })()} />
          <Meta label="Source" value={p.source_tag || p.source_name} />
          <Meta label="Item Code" value={p.item_code} />
        </div>
      </div>
      )}

      {/* Comparison table — full breakup for non-AL pieces. CS 21.80:
          AL-series pieces render a clean FINAL-PRICE section instead (the
          server has already stripped the breakup fields). */}
      {alFinalOnly ? (
        <div className="luxe-card p-6" data-testid="al-final-prices">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C]">Price</p>
          <h3 className="font-serif text-2xl text-[#0D1B2A] mt-1 mb-6">Ashth Lite</h3>
          <div className="space-y-4">
            {columns.map((c) => (
              <div key={c.key} className="flex items-baseline justify-between gap-6" data-testid={`al-price-${c.key}`}>
                <div className="text-[12px] tracking-[0.08em] text-slate-600 font-semibold uppercase">{alPriceLabels[c.key] || c.label}</div>
                <div className="text-[24px] font-semibold text-[#0D1B2A] tabular-nums leading-none tracking-tight">{rupees(c.data.total)}</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
      <div className="luxe-card p-0 overflow-hidden">
        <div className="p-6 pb-3">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C]">Pricing Breakdown</p>
          <h3 className="font-serif text-2xl text-[#0D1B2A] mt-1">Cost & Sell Analysis</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="pricing-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-[#0D1B2A]/8">
                <th className="px-6 py-3 text-left font-medium">Cost Component</th>
                {columns.map((c) => (
                  <th key={c.key} className={`px-6 py-3 text-right font-medium ${c.accent ? "bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]" : ""}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="font-sans font-semibold tabular-nums">
              <Row label="Metal Cost" values={columns.map(() => pr.metal_cost)} columns={columns} />
              <Row label="Labour" values={columns.map((c) => c.data.labour)} columns={columns} />
              <Row label="Diamond Cost" values={columns.map((c) => c.data.diamond)} columns={columns} />

              <tr className="border-t border-[#0D1B2A]/8">
                <td className="px-6 py-3 text-slate-600 font-normal">Sub-total</td>
                {columns.map((c) => (
                  <td key={c.key} className={`px-6 py-3 text-right ${c.accent ? "bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]" : ""}`}>{rupees(c.data.subtotal)}</td>
                ))}
              </tr>
              <tr>
                <td className="px-6 py-3 text-slate-600 font-normal">GST ({gstPct}%)</td>
                {columns.map((c) => (
                  <td key={c.key} className={`px-6 py-3 text-right ${c.accent ? "bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]" : ""}`}>
                    {c.key === "customer" ? rupees(pr.customer.gst) : <span className="text-slate-300">—</span>}
                  </td>
                ))}
              </tr>

              <tr className="border-t-2 border-[#C9A84C]/50 bg-[#0D1B2A]/3">
                <td className="px-6 py-4 font-serif text-[#0D1B2A] uppercase tracking-wider text-xs">Total</td>
                {columns.map((c) => (
                  <td key={c.key} className={`px-6 py-4 text-right font-sans font-bold text-lg text-[#0D1B2A] ${c.accent ? "bg-[#C9A84C]/10 border-l-2 border-[#C9A84C]" : ""}`}>
                    {rupees(c.data.total)}
                    {c.key === "customer" && <div className="text-[10px] text-slate-400 font-normal italic mt-0.5">Rounded to ₹100</div>}
                  </td>
                ))}
              </tr>

              {/* GP rows — show GP for every visible non-customer column.
                  CS 21.85 (v7): distributor owner additionally gets a dedicated
                  "GP vs Retailer" row in the Distributor Cost (franchise) column.
                  Legitimate zero GP is preserved (not treated as missing);
                  only truly null/undefined renders "—". */}
              {(showCompany || showFranchise || showRetailer) && (
                <>
                  {pr.gp?.distributor_vs_retailer && (
                    <tr className="border-t border-[#0D1B2A]/8" data-testid="gp-dist-vs-retailer-row">
                      <td className="px-6 py-3 text-slate-600 font-normal">GP vs Retailer</td>
                      {columns.map((c) => {
                        const show = c.key === "franchise";
                        const amt = show ? pr.gp.distributor_vs_retailer.amount : null;
                        const pct = show ? pr.gp.distributor_vs_retailer.pct : null;
                        return (
                          <td key={c.key} className={`px-6 py-3 text-right ${c.accent ? "bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]" : ""}`}>
                            {show && amt != null ? (
                              <div>
                                <div>{rupees(amt)}</div>
                                {pct != null ? (
                                  <div className="text-[9px] text-[#C9A84C] font-normal">{pct.toFixed(1)}% margin</div>
                                ) : (
                                  <div className="text-[9px] text-slate-400 font-normal">N/A</div>
                                )}
                              </div>
                            ) : <span className="text-slate-300">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                  <tr className="border-t border-[#0D1B2A]/8">
                    <td className="px-6 py-3 text-slate-600 font-normal">GP vs Customer MRP</td>
                    {columns.map((c) => {
                      let v = null;
                      let next = null;
                      if (c.key === "company") {
                        v = pr.gp?.company_gp;
                        next = pr.gp?.company_gp_label;
                      } else if (c.key === "franchise") {
                        v = pr.gp?.franchise_gp;
                        next = pr.gp?.franchise_gp_label;
                      } else if (c.key === "retailer") {
                        v = pr.gp?.retailer_gp;
                        next = pr.gp?.retailer_gp_label;
                      }
                      return (
                        <td key={c.key} className={`px-6 py-3 text-right ${c.accent ? "bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]" : ""}`}>
                          {v != null ? (
                            <div>
                              <div>{rupees(v)}</div>
                              {next && <div className="text-[9px] text-slate-400 font-normal">vs {next}</div>}
                            </div>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className="px-6 py-3 text-slate-600 font-normal">GP %</td>
                    {columns.map((c) => {
                      let v = null;
                      if (c.key === "company") v = pr.gp?.company_gp_pct;
                      else if (c.key === "franchise") v = pr.gp?.franchise_gp_pct;
                      else if (c.key === "retailer") v = pr.gp?.retailer_gp_pct;
                      return <td key={c.key} className={`px-6 py-3 text-right ${c.accent ? "bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]" : ""}`}>{v != null ? <span className="text-[#C9A84C]">{v.toFixed(1)}%</span> : <span className="text-slate-300">—</span>}</td>;
                    })}
                  </tr>
                </>
              )}
              {/* CS 21.23 -- distributor-channel staff/team payloads never carry
                  per-carat rates, so the row stays hidden for them (all "—").
                  CS 21.84 (v6) -- owner roles (distributor owner / retailer owner)
                  DO receive server-gated per-column avg rates; the row renders
                  whenever at least one visible column carries a value. */}
              {(!isDistributorChannel || columns.some((c) => c.data.avg_dia_rate != null)) && (
                <tr className="border-t border-[#0D1B2A]/8" data-testid="avg-dia-rate-row">
                  <td className="px-6 py-3 text-slate-600 font-normal">
                    {isDistributorChannel ? "Avg Diamond Cost/Charge per ct" : "Avg Diamond Rate/ct"}
                  </td>
                  {columns.map((c) => (
                    <td key={c.key} className={`px-6 py-3 text-right ${c.accent ? "bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]" : ""}`}>
                      {c.data.avg_dia_rate != null ? (
                        <div>
                          <div>{rupees(c.data.avg_dia_rate)}/ct</div>
                          {isDistributorChannel && (
                            <div className="text-[9px] text-slate-400 font-normal">
                              {c.key === "customer" ? "charge" : "cost"}
                            </div>
                          )}
                        </div>
                      ) : <span className="text-slate-300" title="No diamonds — not applicable">N/A</span>}
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Customer discount calculator — visible to anyone who can see Customer MRP.
          CS 21.80: hidden for AL-series — the rate-override mode and its share
          caption expose per-carat rates and a full price breakup, which AL
          visibility rules forbid. */}
      {!alFinalOnly && <DiscountCalculator product={p} pricing={pr} rateMeta={rm} />}

      {/* Diamond details — CS 21.83 (v5): for AL pieces this is a PHYSICAL
          specification section (shape / pcs / ct per line), always visible,
          with NO monetary or rate columns. Non-AL pieces keep the existing
          collapsible with role-gated rate columns. */}
      <div className="luxe-card p-6">
        {alFinalOnly ? (
          <div data-testid="al-diamond-breakup">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C]">Diamond Breakup</p>
            <h3 className="font-serif text-2xl text-[#0D1B2A] mt-1">{formatIndian(pr.total_diamond_pcs)} pcs · {formatIndian(pr.total_diamond_ct, { decimals: 3 })} ct</h3>
            <div className="mt-4 divide-y divide-[#0D1B2A]/8">
              {(pr.diamond_lines || []).map((l) => (
                <div key={l.line_number} className="flex items-center justify-between gap-4 py-3 text-sm" data-testid={`al-diamond-line-${l.line_number}`}>
                  <span className="text-[#0D1B2A] font-medium">{l.shape_code || "—"}</span>
                  <span className="text-slate-500 tabular-nums">{l.pieces} pcs · {formatIndian(l.weight_ct, { decimals: 3 })} ct</span>
                </div>
              ))}
              {(!pr.diamond_lines || pr.diamond_lines.length === 0) && (
                <div className="py-3 text-sm text-slate-400" data-testid="al-diamond-empty">No diamonds</div>
              )}
            </div>
          </div>
        ) : (
        <>
        <button onClick={() => setShowDiamonds(!showDiamonds)} data-testid="toggle-diamonds-btn" className="w-full flex items-center justify-between">
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C]">Diamonds</p>
            <h3 className="font-serif text-2xl text-[#0D1B2A] mt-1">{formatIndian(pr.total_diamond_pcs)} pcs · {formatIndian(pr.total_diamond_ct, { decimals: 3 })} ct</h3>
          </div>
          {showDiamonds ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
        </button>
        {showDiamonds && (
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm tabular-nums">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="py-3 pr-4 font-medium">#</th>
                  <th className="py-3 pr-4 font-medium">Shape</th>
                  <th className="py-3 pr-4 text-right font-medium">Pcs</th>
                  <th className="py-3 pr-4 text-right font-medium">Total Wt</th>
                  {!alFinalOnly && <th className="py-3 pr-4 text-right font-medium">Avg</th>}
                  {/* CS 21.23 -- Bracket column hidden for distributor channel. Redacted contract.
                      CS 21.80 -- also hidden for AL-series (rate-table info is breakup-adjacent). */}
                  {!isDistributorChannel && !alFinalOnly && <th className="py-3 pr-4 font-medium">Bracket</th>}
                  {showCompany && pr.diamond_lines[0]?.cost_rate !== undefined && <th className="py-3 pr-4 text-right font-medium">Cost Rate</th>}
                  {pr.diamond_lines[0]?.sell_rate !== undefined && <th className="py-3 pr-4 text-right font-medium">Sell Rate</th>}
                  {showCompany && pr.diamond_lines[0]?.company_amount !== undefined && <th className="py-3 pr-4 text-right font-medium">Co. Cost</th>}
                  {showFranchise && pr.diamond_lines[0]?.franchise_amount !== undefined && <th className="py-3 text-right font-medium">Fr. Cost</th>}
                </tr>
              </thead>
              <tbody className="font-semibold">
                {pr.diamond_lines.map((l) => {
                  const isUnknown = typeof l.shape_code === "string" && l.shape_code.startsWith("UNKNOWN:");
                  const rawShape = isUnknown ? l.shape_code.replace(/^UNKNOWN:/, "") : l.shape_code;
                  return (
                  <tr key={l.line_number} className={`border-t border-[#0D1B2A]/5 ${isUnknown ? "bg-amber-50/40" : ""}`}>
                    <td className="py-3 pr-4 text-slate-400 font-normal">{l.line_number}</td>
                    <td className="py-3 pr-4">
                      {isUnknown ? (
                        <span className="inline-flex items-center gap-1.5 text-amber-700">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          <span className="font-normal italic">{rawShape}</span>
                          <span className="text-[9px] uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full tracking-wider">unknown</span>
                        </span>
                      ) : l.shape_code}
                    </td>
                    <td className="py-3 pr-4 text-right">{l.pieces}</td>
                    <td className="py-3 pr-4 text-right">{formatIndian(l.weight_ct, { decimals: 3 })} ct</td>
                    {!alFinalOnly && <td className="py-3 pr-4 text-right text-slate-500 font-normal">{formatIndian(l.avg_size, { decimals: 4 })}</td>}
                    {!isDistributorChannel && !alFinalOnly && <td className="py-3 pr-4 text-xs text-slate-500 font-normal">{l.bracket}</td>}
                    {showCompany && l.cost_rate !== undefined && <td className="py-3 pr-4 text-right">{rupees(l.cost_rate)}</td>}
                    {l.sell_rate !== undefined && <td className="py-3 pr-4 text-right">{rupees(l.sell_rate)}</td>}
                    {showCompany && l.company_amount !== undefined && <td className="py-3 pr-4 text-right">{rupees(l.company_amount)}</td>}
                    {showFranchise && l.franchise_amount !== undefined && <td className="py-3 text-right">{rupees(l.franchise_amount)}</td>}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}
      </div>

      <Button
        onClick={shareWhatsApp}
        data-testid="whatsapp-share-btn"
        className="w-full h-12 bg-[#25D366] hover:bg-[#1ebc59] text-white font-medium"
      >
        <MessageCircle className="w-5 h-5 mr-2" strokeWidth={1.5} /> Share to Customer
      </Button>

      {!alFinalOnly && Number(rm?.metal_rate || rm?.gold_rate) > 0 && typeof rm?.version === "number" && (
      <p className="text-xs text-slate-400 px-2" data-testid="rate-footnote">
        Calculated using {rm.metal_label || "Gold"} {rupees(rm.metal_rate || rm.gold_rate)}/g · GST {gstPct}% · Rate version {rm.version}
      </p>
      )}
    </div>
  );
}

function Row({ label, values, columns }) {
  return (
    <tr className="border-t border-[#0D1B2A]/5 first:border-t-0">
      <td className="px-6 py-3 text-slate-600 font-normal">{label}</td>
      {values.map((v, i) => (
        <td key={columns[i].key} className={`px-6 py-3 text-right ${columns[i].accent ? "bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]" : ""}`}>{rupees(v)}</td>
      ))}
    </tr>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-sm text-[#0D1B2A] mt-1 truncate font-medium">{value}</div>
    </div>
  );
}
